import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { Fingerprint, KeyRound, Loader2, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { passkeyList, passkeyRegisterOptions, passkeyRegisterVerify, passkeyDelete } from "../lib/api";

export const PasskeysRow = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["passkeys"], queryFn: passkeyList });
  const [busy, setBusy] = useState("");
  const supported = typeof window !== "undefined" && browserSupportsWebAuthn();
  const add = async () => {
    setBusy("add");
    try {
      const options = await passkeyRegisterOptions();
      const cred = await startRegistration({ optionsJSON: options });
      const label = window.prompt("Name this passkey (e.g. iPhone, YubiKey, Work laptop)", "") || "";
      const r = await passkeyRegisterVerify(cred, label);
      qc.invalidateQueries({ queryKey: ["passkeys"] });
      toast.success(`Passkey "${r.label}" added — it's now required to unlock as owner`);
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Cancelled or timed out" : e?.response?.data?.detail || e?.message || "Couldn't add passkey");
    } finally { setBusy(""); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove "${row.label}"? ${data.items.length === 1 ? "Owner unlock will fall back to PIN (and email code if enabled)." : ""}`)) return;
    setBusy(row.id);
    try { await passkeyDelete(row.id); qc.invalidateQueries({ queryKey: ["passkeys"] }); toast.success("Passkey removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
    finally { setBusy(""); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="passkeys-row">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Fingerprint className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">Passkeys & security keys</div>
          <p className="text-xs text-muted-foreground mt-0.5">Face ID, Touch ID, Windows Hello or a hardware key (YubiKey) as the owner's second step after the PIN. Phishing-resistant; nothing to type. Included on every plan.</p>
          {!supported && <p className="text-xs text-amber-700 mt-1" data-testid="passkeys-unsupported">This browser doesn't support passkeys.</p>}
          {data?.items?.length > 0 && (
            <ul className="mt-3 space-y-1.5" data-testid="passkeys-list">
              {data.items.map((k) => (
                <li key={k.id} className="flex items-center gap-2 text-sm rounded-xl border border-border/70 px-3 py-1.5" data-testid="passkey-item">
                  <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-medium">{k.label}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{k.last_used ? `used ${new Date(k.last_used).toLocaleDateString()}` : `added ${new Date(k.created_at).toLocaleDateString()}`}</span>
                  <button type="button" onClick={() => remove(k)} disabled={!!busy} className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${k.label}`} data-testid="passkey-remove-btn">{busy === k.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 shrink-0" onClick={add} disabled={!!busy || !supported} data-testid="passkey-add-btn">{busy === "add" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add passkey</Button>
      </div>
    </div>
  );
};
